/**
 * Thumbnail Service
 * Coordinates fetching thumbnails from storage vs generating them locally
 * Uses fetch-first approach: try storage, only generate on failure
 */

import { thumbnailGenerator } from './thumbnail-generator.js';
import { authService } from '../auth/auth-service.js';
import { SUPABASE_URL, isConfigured } from '../config/config.js';

const THUMBNAIL_BUCKET = 'visualizer-thumbnails';
const THUMBNAIL_VERSION = 'v1';

/**
 * Result type for thumbnail operations
 * @typedef {Object} ThumbnailResult
 * @property {string|null} url - Blob URL or null if unavailable
 * @property {'cache'|'storage'|'generated'|'fallback'} source - Where the thumbnail came from
 */

class ThumbnailService {
  constructor() {
    /** @type {Map<string, {static?: Blob, preview?: Blob, staticUrl?: string, previewUrl?: string}>} */
    this.cache = new Map();
    /** @type {Map<string, Promise<ThumbnailResult>>} */
    this.pendingOperations = new Map();
    /** @type {Set<string>} */
    this.failedGenerations = new Set(); // Track visualizers that failed to generate
    /** @type {Set<string>} */
    this._fetchAttempts = new Set(); // Track paths we've attempted to fetch (for cache-busting)
  }

  /**
   * Get the Supabase client from auth service
   * @returns {Object|null}
   */
  getSupabaseClient() {
    return authService.supabase;
  }

  /**
   * Get static thumbnail - fetch from storage, generate if missing
   * @param {string} visualizerName
   * @returns {Promise<ThumbnailResult>}
   */
  async getStaticThumbnail(visualizerName) {
    // Check cache first — reuse existing blob URL to prevent leaks
    const cached = this.cache.get(visualizerName);
    if (cached?.static) {
      if (!cached.staticUrl) {
        cached.staticUrl = URL.createObjectURL(cached.static);
      }
      return { url: cached.staticUrl, source: 'cache' };
    }

    // Avoid duplicate operations
    const opKey = `static:${visualizerName}`;
    if (this.pendingOperations.has(opKey)) {
      return this.pendingOperations.get(opKey);
    }

    const operation = this._fetchOrGenerate(visualizerName, 'static');
    this.pendingOperations.set(opKey, operation);

    try {
      return await operation;
    } finally {
      this.pendingOperations.delete(opKey);
    }
  }

  /**
   * Get preview video - fetch from storage, generate if missing
   * @param {string} visualizerName
   * @returns {Promise<ThumbnailResult>}
   */
  async getPreviewVideo(visualizerName) {
    // Check cache first — reuse existing blob URL to prevent leaks
    const cached = this.cache.get(visualizerName);
    if (cached?.preview) {
      if (!cached.previewUrl) {
        cached.previewUrl = URL.createObjectURL(cached.preview);
      }
      return { url: cached.previewUrl, source: 'cache' };
    }

    // Avoid duplicate operations
    const opKey = `preview:${visualizerName}`;
    if (this.pendingOperations.has(opKey)) {
      return this.pendingOperations.get(opKey);
    }

    const operation = this._fetchOrGenerate(visualizerName, 'preview');
    this.pendingOperations.set(opKey, operation);

    try {
      return await operation;
    } finally {
      this.pendingOperations.delete(opKey);
    }
  }

  /**
   * Wait for auth service to initialize (max 5 seconds)
   * @returns {Promise<boolean>} - Whether user is authenticated
   */
  async _waitForAuth() {
    // If already initialized, return immediately
    if (authService.initialized) {
      return authService.isAuthenticated();
    }

    // Wait for auth to initialize (max 5 seconds)
    const maxWait = 5000;
    const startTime = Date.now();
    while (!authService.initialized && (Date.now() - startTime) < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return authService.isAuthenticated();
  }

  /**
   * Fetch from storage first, generate locally only on failure
   * @param {string} visualizerName
   * @param {'static'|'preview'} type
   * @returns {Promise<ThumbnailResult>}
   */
  async _fetchOrGenerate(visualizerName, type) {
    const filename = type === 'static' ? 'static.webp' : 'preview.webm';
    const storagePath = `${visualizerName}/${THUMBNAIL_VERSION}/${filename}`;

    // Try storage first (with timeout to avoid slow network blocking UI)
    try {
      const blob = await this._fetchFromStorage(storagePath);
      if (blob) {
        this._cacheResult(visualizerName, type, blob);
        return { url: URL.createObjectURL(blob), source: 'storage' };
      }
    } catch (err) {
      // Storage fetch failed - fall through to generation
      console.debug(`[ThumbnailService] Storage miss for ${visualizerName}/${type}:`, err.message);
    }

    // Storage failed/missing - generate locally (only if authenticated)
    const isAuthenticated = await this._waitForAuth();
    if (!isAuthenticated || this.failedGenerations.has(`${visualizerName}:${type}`)) {
      return { url: null, source: 'fallback' };
    }

    try {
      const blob = await this._generateThumbnail(visualizerName, type);
      this._cacheResult(visualizerName, type, blob);

      // Upload to storage for next time (don't await)
      this._uploadToStorage(visualizerName, type, blob)
        .then(() => console.log(`[ThumbnailService] Uploaded ${visualizerName}/${type}`))
        .catch(err => console.warn(`[ThumbnailService] Upload failed:`, err.message));

      return { url: URL.createObjectURL(blob), source: 'generated' };
    } catch (err) {
      console.warn(`[ThumbnailService] Generation failed for ${visualizerName}:${type}:`, err.message);
      this.failedGenerations.add(`${visualizerName}:${type}`);
      return { url: null, source: 'fallback' };
    }
  }

  /**
   * Fetch thumbnail from Supabase storage with timeout
   * @param {string} path
   * @param {number} timeoutMs - Timeout in milliseconds (default 3000)
   * @returns {Promise<Blob>}
   */
  async _fetchFromStorage(path, timeoutMs = 3000) {
    if (!isConfigured()) {
      throw new Error('Supabase not configured');
    }

    const supabase = this.getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    // Use public URL for fetching (doesn't require auth)
    // Add cache-busting for first load to avoid stale 400 responses
    const cacheBuster = this._fetchAttempts.has(path) ? '' : `?t=${Date.now()}`;
    this._fetchAttempts.add(path);
    const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/${THUMBNAIL_BUCKET}/${path}${cacheBuster}`;

    // Timeout to prevent slow network from blocking UI
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(publicUrl, { signal: controller.signal });

      if (!response.ok) {
        throw new Error(`Storage fetch failed: ${response.status}`);
      }

      return response.blob();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Generate thumbnail locally
   * @param {string} visualizerName
   * @param {'static'|'preview'} type
   * @returns {Promise<Blob>}
   */
  async _generateThumbnail(visualizerName, type) {
    if (type === 'static') {
      return thumbnailGenerator.generateStaticThumbnail(visualizerName);
    } else {
      return thumbnailGenerator.generatePreviewVideo(visualizerName);
    }
  }

  /**
   * Upload generated thumbnail to Supabase via Edge Function
   * @param {string} visualizerName
   * @param {'static'|'preview'} type
   * @param {Blob} blob
   * @returns {Promise<void>}
   */
  async _uploadToStorage(visualizerName, type, blob) {
    if (!isConfigured()) return;

    // Get fresh session to ensure token is valid
    const supabase = this.getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      throw new Error('Not authenticated');
    }

    const accessToken = session.access_token;
    const filename = type === 'static' ? 'static.webp' : 'preview.webm';

    // Upload via Edge Function (for validation)
    const formData = new FormData();
    formData.append('file', blob, filename);
    formData.append('visualizerName', visualizerName);
    formData.append('type', type);

    const response = await fetch(`${SUPABASE_URL}/functions/v1/upload-thumbnail`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: formData
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${text}`);
    }
  }

  /**
   * Cache thumbnail blob
   * @param {string} visualizerName
   * @param {'static'|'preview'} type
   * @param {Blob} blob
   */
  _cacheResult(visualizerName, type, blob) {
    const existing = this.cache.get(visualizerName) || {};
    if (type === 'static') {
      existing.static = blob;
    } else {
      existing.preview = blob;
    }
    this.cache.set(visualizerName, existing);
  }

  /**
   * Clear all cached thumbnails
   */
  clearCache() {
    // Revoke all cached blob URLs to prevent memory leaks
    for (const entry of this.cache.values()) {
      if (entry.staticUrl) URL.revokeObjectURL(entry.staticUrl);
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    }
    this.cache.clear();
    this.failedGenerations.clear();
    this._fetchAttempts.clear();
  }

  /**
   * Clear cached thumbnails for a specific visualizer
   * Used by admin regeneration to force re-fetch/re-generation
   * @param {string} visualizerName
   */
  clearCacheFor(visualizerName) {
    const entry = this.cache.get(visualizerName);
    if (entry) {
      if (entry.staticUrl) URL.revokeObjectURL(entry.staticUrl);
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    }
    this.cache.delete(visualizerName);
    this.failedGenerations.delete(`${visualizerName}:static`);
    this.failedGenerations.delete(`${visualizerName}:preview`);
    // Clear fetch attempts for this visualizer's paths
    const staticPath = `${visualizerName}/v1/static.webp`;
    const previewPath = `${visualizerName}/v1/preview.webm`;
    this._fetchAttempts.delete(staticPath);
    this._fetchAttempts.delete(previewPath);
  }

  /**
   * Prefetch thumbnails for a list of visualizers
   * Useful for warming the cache in the background
   * @param {string[]} visualizerNames
   */
  async prefetchStatic(visualizerNames) {
    await Promise.all(
      visualizerNames.map(name =>
        this.getStaticThumbnail(name).catch(() => null)
      )
    );
  }

  /**
   * Upload a thumbnail to storage (public method for external use)
   * Used by custom visualizer creation to upload generated previews
   * @param {string} visualizerName
   * @param {'static'|'preview'} type
   * @param {Blob} blob
   * @returns {Promise<void>}
   */
  async uploadToStorage(visualizerName, type, blob) {
    await this._uploadToStorage(visualizerName, type, blob);
    // Also cache the result locally
    this._cacheResult(visualizerName, type, blob);
  }
}

export const thumbnailService = new ThumbnailService();
